import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { format, parseISO } from 'date-fns';

const money = (n) =>
  typeof n === 'number' && !Number.isNaN(n)
    ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
    : '—';

const dt = (s) => {
  if (!s) return '—';
  try { return format(parseISO(s), 'PPp'); } catch { return String(s); }
};

const hasValue = (v) =>
  v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);

const Row = ({ label, value, mono }) => (
  <div className="flex text-xs py-1.5 border-b border-border/50 last:border-b-0">
    <span className="w-40 text-muted-foreground shrink-0">{label}</span>
    <span
      className={`flex-1 min-w-0 ${mono ? 'font-mono text-[11px]' : ''} break-all`}
      data-testid={`bd-row-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {hasValue(value) ? value : '—'}
    </span>
  </div>
);

const Section = ({ title, children, testId }) => (
  <div
    className="rounded-lg border border-border bg-card/40 p-3"
    data-testid={testId || `bd-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
      {title}
    </p>
    {children}
  </div>
);

/**
 * Full read-only detail sheet for a booking (admin-only).
 * The parent (BookingsTab) enriches the booking object with venue_name,
 * bookable_name, user_name, etc. before passing it in — legacy bookings
 * that stored only IDs still render human-readable rows.
 */
export const BookingDetailsModal = ({ open, onClose, booking }) => {
  if (!booking) return null;
  const b = booking;

  const hasGuestInfo =
    hasValue(b.guest_name) || hasValue(b.user_name) ||
    hasValue(b.guest_email) || hasValue(b.user_email) ||
    hasValue(b.guest_phone) || hasValue(b.company_name || b.billing_company) || hasValue(b.gstin || b.billing_gstin);

  const hasExternalRefs =
    hasValue(b.razorpay_order_id) || hasValue(b.razorpay_payment_id) ||
    hasValue(b.razorpay_payment_link_id) || hasValue(b.razorpay_payment_link_url) ||
    hasValue(b.balance_razorpay_payment_link_id) || hasValue(b.balance_razorpay_payment_link_url) ||
    hasValue(b.zoho_invoice_number) || hasValue(b.zoho_invoice_id);

  const hasAddOns = b.add_ons && typeof b.add_ons === 'object' && Object.keys(b.add_ons).length > 0;
  const hasNotes = hasValue(b.notes) || hasValue(b.description) || hasAddOns || hasValue(b.cancellation_reason);

  const rescheduleLog = Array.isArray(b.reschedule_log) ? b.reschedule_log : [];
  const repairLog = Array.isArray(b.admin_repair_log) ? b.admin_repair_log : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="booking-details-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-muted-foreground">
              #{(b.id || '').slice(0, 8) || '—'}
            </span>
            <span data-testid="bd-title-name">
              {b.guest_name || b.user_name || b.company_name || b.organization_name || 'Client booking'}
            </span>
            {b.status && (
              <Badge variant="outline" className="text-[10px]" data-testid="bd-title-status">
                {b.status.replace(/_/g, ' ')}
              </Badge>
            )}
            {b.booking_type && (
              <Badge variant="outline" className="text-[10px]" data-testid="bd-title-type">
                {b.booking_type}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          {hasGuestInfo && (
            <Section title="Guest / Client">
              <Row label="Name" value={b.guest_name || b.user_name} />
              <Row label="Email" value={b.guest_email || b.user_email} />
              <Row label="Phone" value={b.guest_phone} />
              {/* billing_* = checkout bookings made before they were also saved as company_name/gstin */}
              <Row label="Company" value={b.company_name || b.billing_company} />
              <Row label="GSTIN" value={b.gstin || b.billing_gstin} mono />
              <Row label="Organization" value={b.organization_name || b.organization_id} />
            </Section>
          )}

          <Section title="Space + Time">
            <Row label="Venue" value={b.venue_name} />
            <Row label="Region" value={b.region} />
            <Row label="Space" value={b.bookable_name} />
            <Row label="Space ID" value={b.bookable_id} mono />
            {b.end_date ? (
              <Row label="Membership Period" value={`${b.booking_date} – ${b.end_date} (1 month)`} />
            ) : (
              <>
                <Row label="Date" value={b.booking_date} />
                <Row
                  label="Time"
                  value={
                    b.start_time && b.end_time
                      ? `${b.start_time} – ${b.end_time}`
                      : b.start_time || b.end_time || null
                  }
                />
                <Row label="Duration" value={b.hours ? `${b.hours} hours` : null} />
              </>
            )}
            <Row label="Attendees" value={b.attendees} />
            {(b.pass_quantity > 1 || b.pass_days > 1) && (
              <Row
                label="Day pass"
                value={`${b.pass_quantity || 1} pass${(b.pass_quantity || 1) > 1 ? 'es' : ''} · valid ${b.pass_days || 1} day${(b.pass_days || 1) > 1 ? 's' : ''}${b.is_bulk_pass ? ' (bulk discount)' : ''}`}
              />
            )}
          </Section>

          <Section title="Amount + Payment">
            <Row label="Base amount" value={money(b.base_amount)} />
            {hasValue(b.add_ons_amount) && b.add_ons_amount !== 0 && (
              <Row label="Add-ons" value={money(b.add_ons_amount)} />
            )}
            {hasValue(b.auto_discount_amount) && b.auto_discount_amount !== 0 && (
              <Row
                label="Auto discount"
                value={`${money(b.auto_discount_amount)} (${b.auto_discount_pct || 0}%)`}
              />
            )}
            {hasValue(b.discount_amount) && b.discount_amount !== 0 && (
              <Row
                label="Manual discount"
                value={`${money(b.discount_amount)} (${b.discount_percent || 0}%)`}
              />
            )}
            {hasValue(b.coupon_code) && <Row label="Coupon" value={b.coupon_code} />}
            {hasValue(b.gst_amount) && b.gst_amount !== 0 && (
              <Row label="GST" value={`${money(b.gst_amount)} (${b.gst_rate || 18}%)`} />
            )}
            <Row label="Total" value={<b>{money(b.total_amount)}</b>} />
            {(b.credits_used || 0) > 0 && (
              <Row label="Credits used" value={b.credits_used} />
            )}
            <Row label="Payment type" value={b.payment_type} />
            <Row label="Payment status" value={b.payment_status} />
            <Row label="Payment method" value={b.payment_method} />
            {b.is_partial_payment && (
              <>
                <Row label="Partial amount" value={money(b.partial_amount)} />
                <Row label="Partial paid" value={b.partial_paid ? `Yes · ${dt(b.partial_paid_at)}` : 'No'} />
                <Row label="Balance amount" value={money(b.balance_amount)} />
                <Row label="Balance due" value={b.balance_due_date} />
                <Row label="Balance paid" value={b.balance_paid ? `Yes · ${dt(b.balance_paid_at)}` : 'No'} />
              </>
            )}
          </Section>

          {hasExternalRefs && (
            <Section title="External refs (Razorpay / Zoho)">
              <Row label="Razorpay order" value={b.razorpay_order_id} mono />
              <Row label="Razorpay payment" value={b.razorpay_payment_id} mono />
              <Row label="Razorpay link id" value={b.razorpay_payment_link_id} mono />
              <Row
                label="Razorpay link URL"
                value={
                  b.razorpay_payment_link_url ? (
                    <a
                      href={b.razorpay_payment_link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      data-testid="bd-razorpay-link"
                    >
                      Open
                    </a>
                  ) : null
                }
              />
              {b.is_partial_payment && (
                <>
                  <Row label="Balance link id" value={b.balance_razorpay_payment_link_id} mono />
                  <Row
                    label="Balance link URL"
                    value={
                      b.balance_razorpay_payment_link_url ? (
                        <a
                          href={b.balance_razorpay_payment_link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                          data-testid="bd-balance-razorpay-link"
                        >
                          Open
                        </a>
                      ) : null
                    }
                  />
                  <Row label="Balance payment id" value={b.balance_razorpay_payment_id} mono />
                </>
              )}
              <Row label="Zoho invoice #" value={b.zoho_invoice_number} />
              <Row label="Zoho invoice id" value={b.zoho_invoice_id} mono />
              {hasValue(b.zoho_invoice_id) && (
                <Row
                  label="Zoho payment recorded"
                  value={b.zoho_payment_recorded ? 'Yes' : 'No'}
                />
              )}
            </Section>
          )}

          <Section title="Attribution + timestamps">
            <Row label="Created by" value={b.created_by_name || b.created_by} />
            <Row label="Created by role" value={b.created_by_role} />
            <Row label="Booked for" value={b.booked_for} />
            <Row label="Is internal" value={b.is_internal ? 'Yes' : 'No'} />
            <Row label="Created at" value={dt(b.created_at)} />
            <Row label="Updated at" value={dt(b.updated_at)} />
            {hasValue(b.updated_by) && (
              b.updated_by_name
                ? <Row label="Updated by" value={b.updated_by_role ? `${b.updated_by_name} (${b.updated_by_role})` : b.updated_by_name} />
                : <Row label="Updated by" value={b.updated_by} mono />
            )}
          </Section>

          {hasNotes && (
            <Section title="Notes + description">
              {hasValue(b.notes) && <Row label="Notes" value={b.notes} />}
              {hasValue(b.description) && <Row label="Description" value={b.description} />}
              {hasAddOns && (
                <Row
                  label="Add-ons"
                  value={
                    <pre className="text-[10px] font-mono bg-muted/30 rounded p-1 whitespace-pre-wrap">
                      {JSON.stringify(b.add_ons, null, 2)}
                    </pre>
                  }
                />
              )}
              {hasValue(b.cancellation_reason) && (
                <Row label="Cancellation" value={b.cancellation_reason} />
              )}
            </Section>
          )}
        </div>

        {rescheduleLog.length > 0 && (
          <Section
            title={`Reschedule history (${rescheduleLog.length})`}
            testId="bd-section-reschedule-history"
          >
            <div className="space-y-2 mt-1">
              {rescheduleLog.slice().reverse().map((r, i) => (
                <div key={i} className="text-xs p-2 rounded bg-muted/30">
                  <p>
                    <b>{r.by_name || '—'}</b>
                    {r.by_role ? ` (${r.by_role})` : ''} · {dt(r.at)}
                  </p>
                  <p className="text-muted-foreground">
                    <span className="line-through">
                      {r.from?.booking_date} · {r.from?.start_time}–{r.from?.end_time}
                    </span>
                    {' → '}
                    <b>
                      {r.to?.booking_date} · {r.to?.start_time}–{r.to?.end_time}
                    </b>
                  </p>
                  {r.reason && (
                    <p className="italic text-muted-foreground">&ldquo;{r.reason}&rdquo;</p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {repairLog.length > 0 && (
          <Section
            title={`Admin repairs (${repairLog.length})`}
            testId="bd-section-admin-repairs"
          >
            <div className="space-y-2 mt-1">
              {repairLog.slice().reverse().map((r, i) => (
                <div key={i} className="text-xs p-2 rounded bg-muted/30">
                  <p>
                    <b>{r.by_name || '—'}</b> · {dt(r.at)}
                  </p>
                  {r.reason && (
                    <p className="italic text-muted-foreground">&ldquo;{r.reason}&rdquo;</p>
                  )}
                  <ul className="list-disc list-inside mt-1">
                    {(r.changes || []).map((c, j) => (
                      <li key={j}>
                        <b>{c.field}</b>:{' '}
                        <span className="text-muted-foreground">
                          {String(c.from ?? '—')}
                        </span>{' '}
                        → {String(c.to)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>
        )}
      </DialogContent>
    </Dialog>
  );
};
